import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  IconBrandWhatsapp,
  IconBrandX,
  IconBrandLinkedin,
  IconMail,
  IconBrandFacebook,
  IconShare,
  IconCopy,
  IconCheck
} from '@tabler/icons-react';

interface ShareSidebarProps {
  isOpen: boolean;
  shareUrl: string;
  shareTitle: string;
  shareDescription?: string;
}

export function ShareSidebar({ isOpen, shareUrl, shareTitle, shareDescription }: ShareSidebarProps) {
  const [copied, setCopied] = useState(false);

  const shareToFacebook = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`;
    window.open(url, '_blank');
  };

  const shareToEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`${shareDescription || shareTitle}\n\n${shareUrl}`);
    const url = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = url;
  };

  const shareToNative = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription || shareTitle,
          url: shareUrl,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      handleCopy();
    }
  };

  const handleCopy = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block fixed left-0 top-1/2 transform -translate-y-1/2 z-[999999999] pointer-events-auto" data-share-sidebar>
        <div className="bg-black/90 backdrop-blur-sm rounded-r-lg shadow-lg border-r border-white/10">
          <div className="flex flex-col items-center p-2 space-y-2">
            {/* Share Button */}
            <Button
              onClick={shareToNative}
              size="sm"
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg px-3 py-2 text-xs font-medium cursor-pointer"
            >
              <IconShare className="w-4 h-4 mr-1" />
              Share
            </Button>

            {/* Social Icons */}
            <div className="flex flex-col space-y-2">
              <Button
                onClick={shareToFacebook}
                size="sm"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="Share on Facebook"
              >
                <IconBrandFacebook className="w-4 h-4" />
              </Button>

              <Button
                onClick={shareToTwitter}
                size="sm"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="Share on X (Twitter)"
              >
                <IconBrandX className="w-4 h-4" />
              </Button>

              <Button
                onClick={shareToLinkedIn}
                size="sm"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="Share on LinkedIn"
              >
                <IconBrandLinkedin className="w-4 h-4" />
              </Button>

              <Button
                onClick={shareToWhatsApp}
                size="sm"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="Share on WhatsApp"
              >
                <IconBrandWhatsapp className="w-4 h-4" />
              </Button>

              <Button
                onClick={shareToEmail}
                size="sm"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="Share via Email"
              >
                <IconMail className="w-4 h-4" />
              </Button>

              <Button
                onClick={handleCopy}
                size="sm"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="Copy Link"
              >
                {copied ? <IconCheck className="w-4 h-4" /> : <IconCopy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[999999999] pointer-events-auto" data-share-sidebar>
        <div className="bg-black/90 backdrop-blur-sm border-t border-white/10">
          <div className="flex items-center justify-center space-x-1 p-2 px-2 overflow-x-auto">
            <Button
              onClick={shareToNative}
              size="sm"
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg px-2 py-2 text-xs font-medium cursor-pointer flex-shrink-0"
            >
              <IconShare className="w-3 h-3 mr-1" />
              <span className="hidden xs:inline">Share</span>
            </Button>

            <div className="flex space-x-1 flex-shrink-0">
              <Button
                onClick={shareToFacebook}
                size="sm"
                className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="Facebook"
              >
                <IconBrandFacebook className="w-3 h-3 sm:w-4 sm:h-4" />
              </Button>

              <Button
                onClick={shareToTwitter}
                size="sm"
                className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="X (Twitter)"
              >
                <IconBrandX className="w-3 h-3 sm:w-4 sm:h-4" />
              </Button>

              <Button
                onClick={shareToLinkedIn}
                size="sm"
                className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="LinkedIn"
              >
                <IconBrandLinkedin className="w-3 h-3 sm:w-4 sm:h-4" />
              </Button>

              <Button
                onClick={shareToWhatsApp}
                size="sm"
                className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="WhatsApp"
              >
                <IconBrandWhatsapp className="w-3 h-3 sm:w-4 sm:h-4" />
              </Button>

              <Button
                onClick={shareToEmail}
                size="sm"
                className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="Email"
              >
                <IconMail className="w-3 h-3 sm:w-4 sm:h-4" />
              </Button>

              <Button
                onClick={handleCopy}
                size="sm"
                className="w-9 h-9 sm:w-10 sm:h-10 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-lg p-0 cursor-pointer"
                title="Copy Link"
              >
                {copied ? <IconCheck className="w-3 h-3 sm:w-4 sm:h-4" /> : <IconCopy className="w-3 h-3 sm:w-4 sm:h-4" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
